import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function PageHero({
  title,
  subtitle,
  cta1Text,
  cta1Link,
  cta2Text = null,
  cta2Link = null,
  hasGlow = true,
  eyebrow = null,
  gradientWord = null,
}) {
  return (
    <section
      className="hero-glow dotted-grid relative overflow-hidden w-full"
      style={{
        minHeight: "92vh",
        display: "flex",
        alignItems: "center",
        background: "#080808",
      }}
    >
      <style>{`
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        .glow-bg {
          animation: glow-pulse 4s ease-in-out infinite;
        }
      `}</style>

      {/* Gradient glows */}
      {hasGlow && (
        <>
          <div
            className="glow-bg"
            style={{
              position: "absolute",
              top: "5%",
              right: "-10%",
              width: 700,
              height: 700,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(249,115,22,0.12), transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <div
            className="glow-bg"
            style={{
              position: "absolute",
              bottom: "5%",
              left: "-10%",
              width: 600,
              height: 600,
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(139,92,246,0.1), transparent 70%)",
              pointerEvents: "none",
            }}
          />
        </>
      )}

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 w-full relative z-10">
        <div className="max-w-2xl">
          {/* Eyebrow */}
          {eyebrow && (
            <div className="flex items-center gap-2 mb-8">
              <div style={{ height: 1, width: 32, background: "linear-gradient(90deg, #F97316, #8B5CF6)" }} />
              <p
                className="text-sm font-medium tracking-widest uppercase"
                style={{
                  background: "linear-gradient(135deg, #F97316, #A78BFA)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {eyebrow}
              </p>
            </div>
          )}

          {/* Title with optional gradient word */}
          <h1
            className="font-display font-bold leading-none tracking-tight"
            style={{
              fontSize: "clamp(3.5rem, 9vw, 7rem)",
              color: "#F5F5F5",
              marginBottom: "2.5rem",
            }}
          >
            {gradientWord ? (
              <>
                {title.split(gradientWord)[0]}
                <span
                  style={{
                    background: "linear-gradient(135deg, #F97316, #EC4899)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {gradientWord}
                </span>
                {title.split(gradientWord)[1]}
              </>
            ) : (
              title
            )}
          </h1>

          {/* Subtitle */}
          {subtitle && (
            <p
              className="text-xl font-light max-w-xl leading-relaxed mb-12"
              style={{ color: "rgba(245,245,245,0.45)" }}
            >
              {subtitle}
            </p>
          )}

          {/* CTAs */}
          <div className="flex flex-wrap gap-4 items-center">
            <Link
              to={cta1Link}
              className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full text-white"
              style={{
                background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)",
                fontSize: "1rem",
                boxShadow: "0 8px 32px rgba(139,92,246,0.25)",
              }}
            >
              {cta1Text} <ArrowRight className="h-4 w-4" />
            </Link>

            {cta2Text && cta2Link && (
              <Link
                to={cta2Link}
                className="btn-press inline-flex items-center gap-2 font-medium px-7 py-4 rounded-full"
                style={{
                  border: "1px solid rgba(245,245,245,0.12)",
                  color: "rgba(245,245,245,0.7)",
                  fontSize: "1rem",
                }}
              >
                {cta2Text}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
